// Pure helpers for the Org 360 voice assistant settings: labels, the form
// state, validation mirroring the backend's rules, the patch builder, the
// template preview and the recent-calls rows. Framework-free so they can be
// unit-tested without rendering. Allowed values, voices, variables and bounds
// come from the backend's GET /admin/voice-settings/:organizationId
// (`options`, `limits`); the fallbacks here only cover a response without them.

export const MODEL_LABELS = {
  "deepgram:flux-general-en": "Deepgram Flux (fastest turn-taking)",
  "deepgram:nova-3": "Deepgram Nova 3",
  "openai:gpt-4o-mini-transcribe": "OpenAI GPT-4o mini transcribe",
  "openai:gpt-4.1-mini": "OpenAI GPT-4.1 mini",
  "openai:gpt-4o-mini": "OpenAI GPT-4o mini",
  "openai:gpt-5.4-mini": "OpenAI GPT-5.4 mini",
  "azure:en-US-Harper:MAI-Voice-2.1-Flash": "Azure Harper",
  "gemini:gemini-3.1-flash-tts-preview": "Google (Gemini TTS)",
  "xai:tts-1": "xAI",
};

export const TURN_DETECTION_LABELS = {
  stt: "By the speech recogniser (needs Deepgram Flux)",
  vad: "By silence",
};

export const WHO_SPEAKS_FIRST_LABELS = {
  borrower: "Borrower says hello first",
  assistant: "Assistant speaks first",
};

export const BACKGROUND_SOUND_LABELS = {
  off: "Off",
  office: "Office",
  office_typing: "Office + occasional typing",
  busy_office: "Busy open-plan office",
  call_center: "Call center floor",
  cafe: "Café / crowd",
  city: "City",
  forest: "Forest",
  keyboard: "Keyboard typing",
};

export const TRANSFER_BRIEFING_LABELS = {
  auto: "Auto summary (default)",
  custom: "Custom text",
};

export const CALL_OUTCOME_LABELS = {
  answered: "Answered",
  no_answer: "No answer",
  voicemail: "Voicemail",
  rejected: "Not reached",
  trunk_failure: "Not reached",
};

export const SENTIMENT_LABELS = {
  positive: "Positive",
  neutral: "Neutral",
  negative: "Negative",
};

export const DEFAULT_LIMITS = {
  personalityMaxChars: 300,
  promptAdditionsMaxChars: 2000,
  greetingMaxChars: 300,
  voicemailMessageMaxChars: 400,
  destinationNameMaxChars: 80,
  keyTerms: { maxEntries: 30, maxChars: 40 },
  documentExplanations: { maxEntries: 40, matchMaxChars: 60, sendMaxChars: 200 },
  speed: { min: 0.7, max: 1.5 },
  backgroundVolume: { min: 0, max: 1 },
  maxDurationS: { min: 60, max: 900 },
  silenceTimeoutS: { min: 5, max: 60 },
  initialSilenceTimeoutS: { min: 5, max: 60 },
  maxSilenceNudges: { min: 0, max: 3 },
  endpointingMinDelayS: { min: 0, max: 3 },
  endpointingMaxDelayS: { min: 0.5, max: 6 },
  interruptionMinDurationS: { min: 0.2, max: 2 },
  interruptionMinWords: { min: 0, max: 5 },
  vadActivationThreshold: { min: 0.2, max: 0.9 },
  firstMessageDelayS: { min: 0, max: 3 },
};

export const DEFAULT_VOICE_KEY = "azure:en-US-Harper";
export const FLUX_MODEL_PREFIX = "deepgram:flux-";
export const VARIABLE_PATTERN = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;
export const E164 = /^\+[1-9][0-9]{7,14}$/;
export const TIME_OF_DAY = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

export const SAMPLE_VARIABLE_VALUES = {
  first_name: "Jane",
  caller_name: "Jane Doe",
  caller_phone_last4: "2820",
  agent_name: "Addy",
  brand_name: "Sunrise Mortgage",
  loan_officer_name: "Jordan Lee",
  loan_officer_phone_last4: "0142",
  loan_number: "1234567",
  property_state: "CA",
  property_address: "12 Oak Circle",
  documents: "your W-2, a photo ID",
  document_count: "2",
  greeting: "Good morning",
  current_date: "October 17, 2026",
  current_time: "9:15 AM",
  day_of_week: "Friday",
};

export const DEFAULT_GREETING =
  "Hi, this is {{agent_name}}, an AI assistant calling on behalf of {{loan_officer_name}} from {{brand_name}}. Am I speaking with {{first_name}}?";

export function modelLabel(spec) {
  return MODEL_LABELS[spec] || spec || "—";
}

export function turnDetectionLabel(mode) {
  return TURN_DETECTION_LABELS[mode] || mode || "—";
}

export function whoSpeaksFirstLabel(mode) {
  return WHO_SPEAKS_FIRST_LABELS[mode] || mode || "—";
}

export function backgroundSoundLabel(sound) {
  return BACKGROUND_SOUND_LABELS[sound] || sound || "—";
}

export function callOutcomeLabel(outcome) {
  return CALL_OUTCOME_LABELS[outcome] || "Call ended";
}

/** "1:26" for 86 seconds; "—" when unknown. */
export function formatCallDuration(seconds) {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) {
    return "—";
  }
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;

  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

/** "$0.03" for 0.0305; "—" when unknown. */
export function formatCost(usd) {
  if (typeof usd !== "number" || !Number.isFinite(usd)) {
    return "—";
  }

  return `$${usd.toFixed(usd < 0.1 ? 3 : 2)}`;
}

/** True when the backend answered 404 for the route: not deployed in this environment. */
export function isRouteMissing(error) {
  return /→ 404\b/.test(String(error?.message || ""));
}

/** The variable names a template uses, in order, without repeats. */
export function findVariables(template) {
  const names = [];
  for (const match of String(template || "").matchAll(VARIABLE_PATTERN)) {
    if (!names.includes(match[1])) {
      names.push(match[1]);
    }
  }

  return names;
}

/** A template with sample values in place of the variables, for the preview. */
export function renderTemplate(template, values = SAMPLE_VARIABLE_VALUES) {
  return String(template || "")
    .replace(VARIABLE_PATTERN, (_, name) => values[name] ?? "")
    .trim();
}

/** Insert text at the caret of an input or textarea, returning the new value and caret. */
export function insertAtCursor(value, selectionStart, selectionEnd, insertion) {
  const start = Number.isInteger(selectionStart) ? selectionStart : value.length;
  const end = Number.isInteger(selectionEnd) ? selectionEnd : start;
  const next = `${value.slice(0, start)}${insertion}${value.slice(end)}`;

  return { value: next, caret: start + insertion.length };
}

/** The stored settings as the form holds them: every field present, numbers as numbers. */
export function toFormState(settings) {
  const models = settings?.models || {};
  const voice = settings?.voice || {};
  const sound = settings?.sound || {};
  const wording = settings?.wording || {};
  const analysis = settings?.analysis || {};
  const voicemail = settings?.voicemail || {};
  const call = settings?.call || {};
  const transfer = settings?.transfer || {};
  const callingHours = settings?.callingHours || {};

  return {
    stt: models.stt || "",
    llm: models.llm || "",
    voiceKey: voice.key || DEFAULT_VOICE_KEY,
    speed: voice.speed ?? 1,
    expressiveSpeech: voice.expressive !== false,
    backgroundSound: sound.background || "off",
    backgroundVolume: sound.volume ?? 0.1,
    thinkingSound: sound.thinkingSound === true || call.thinkingSound === true,
    agentType: wording.agentType || "document_request",
    greeting: wording.greeting || "",
    personalityPreset: wording.personalityPreset || "",
    personality: wording.personality || "",
    promptAdditions: wording.promptAdditions || "",
    deferFigures: wording.deferFigures !== false,
    keyTerms: Array.isArray(wording.keyTerms) ? wording.keyTerms.join(", ") : "",
    documentExplanations: Array.isArray(wording.documentExplanations)
      ? wording.documentExplanations.map((rule) => ({ match: rule.match || "", send: rule.send || "" }))
      : null,
    postCall: analysis.postCall !== false,
    sentiment: analysis.sentiment === true,
    voicemailDetect: voicemail.detect === true,
    voicemailLeaveMessage: voicemail.leaveMessage === true,
    voicemailMessage: voicemail.message || "",
    maxDurationS: call.maxDurationS ?? 600,
    silenceTimeoutS: call.silenceTimeoutS ?? 15,
    initialSilenceTimeoutS: call.initialSilenceTimeoutS ?? 30,
    maxSilenceNudges: call.maxSilenceNudges ?? 1,
    turnDetection: call.turnDetection || "stt",
    endpointingMinDelayS: call.endpointingMinDelayS ?? 0.1,
    endpointingMaxDelayS: call.endpointingMaxDelayS ?? 3,
    interruptionMinDurationS: call.interruptionMinDurationS ?? 1,
    interruptionMinWords: call.interruptionMinWords ?? 2,
    vadActivationThreshold: call.vadActivationThreshold ?? 0.5,
    whoSpeaksFirst: call.whoSpeaksFirst || "borrower",
    firstMessageDelayS: call.firstMessageDelayS ?? 0.5,
    preemptiveSpeech: call.preemptiveSpeech === true,
    recording: call.recording === true,
    consentLine: call.consentLine === true,
    identityCheck: call.identityCheck !== false,
    forwardingNumber: transfer.forwardingNumber || "",
    forwardImmediately: transfer.forwardImmediately === true,
    transferBriefing: transfer.briefing || "auto",
    destinationName: transfer.destinationName || "",
    transferHoursStart: transfer.hoursStart || "",
    transferHoursEnd: transfer.hoursEnd || "",
    timezone: callingHours.timezone || "",
  };
}

function toNumber(value) {
  const parsed = typeof value === "number" ? value : Number(String(value).trim());

  return Number.isFinite(parsed) ? parsed : null;
}

function within(value, bounds) {
  return value !== null && value >= bounds.min && value <= bounds.max;
}

function isValidTimezone(value) {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: value });

    return true;
  } catch (error) {
    return false;
  }
}

export function splitTerms(text) {
  const seen = new Set();
  const terms = [];
  for (const raw of String(text || "").split(/[,\n]/)) {
    const term = raw.trim();
    if (term && !seen.has(term.toLowerCase())) {
      seen.add(term.toLowerCase());
      terms.push(term);
    }
  }

  return terms;
}

/**
 * The what-to-send rules as the backend takes them: `null` keeps the voice
 * service's standard list, a list is the organization's own (blank rows
 * dropped, whitespace folded).
 */
export function cleanExplanations(rules) {
  if (!Array.isArray(rules)) {
    return null;
  }

  return rules
    .map((rule) => ({
      match: String(rule?.match || "").replace(/\s+/g, " ").trim(),
      send: String(rule?.send || "").replace(/\s+/g, " ").trim(),
    }))
    .filter((rule) => rule.match || rule.send);
}

function unknownVariables(template, known) {
  if (!Array.isArray(known)) {
    return [];
  }

  return findVariables(template).filter((name) => !known.includes(name));
}

/**
 * Validate the form before saving. Returns human-readable errors (empty when
 * valid). The backend re-checks the same rules; this only spares a round trip.
 *
 * @param {object} form - from toFormState
 * @param {object} [options] - from the backend: models, voices, variables, agentTypes, personalityPresets, backgroundSounds, whoSpeaksFirst
 * @param {object} [limits]
 * @returns {string[]}
 */
export function validateVoiceSettingsForm(form, options = {}, limits = DEFAULT_LIMITS) {
  const errors = [];
  const bounds = { ...DEFAULT_LIMITS, ...limits };
  const choices = options.models || {};
  const known = options.variables?.[form.agentType];

  for (const [key, label] of [
    ["stt", "Speech recognition model"],
    ["llm", "Language model"],
  ]) {
    const allowed = Array.isArray(choices[key]) ? choices[key] : null;
    if (!form[key]) {
      errors.push(`${label} is required.`);
    } else if (allowed && !allowed.includes(form[key])) {
      errors.push(`${label} must be one of the offered choices.`);
    }
  }

  if (!form.voiceKey) {
    errors.push("Voice is required.");
  } else if (
    Array.isArray(options.voices) &&
    options.voices.length &&
    !options.voices.some((voice) => voice.key === form.voiceKey)
  ) {
    errors.push("Voice must be one of the offered choices.");
  }

  if (form.turnDetection === "stt" && form.stt && !form.stt.startsWith(FLUX_MODEL_PREFIX)) {
    errors.push(
      "Turn detection by the recogniser needs the Deepgram Flux model; choose it or switch to detection by silence.",
    );
  }

  const activeTypes = Array.isArray(options.agentTypes)
    ? options.agentTypes.filter((type) => type.status === "active").map((type) => type.id)
    : ["document_request"];
  if (!activeTypes.includes(form.agentType)) {
    errors.push("That agent type is not available yet.");
  }
  if (
    form.personalityPreset &&
    Array.isArray(options.personalityPresets) &&
    !options.personalityPresets.some((preset) => preset.id === form.personalityPreset)
  ) {
    errors.push("Personality preset must be one of the offered choices.");
  }
  if (
    Array.isArray(options.backgroundSounds) &&
    !options.backgroundSounds.includes(form.backgroundSound)
  ) {
    errors.push("That background sound is not available yet.");
  }

  for (const [key, label, bound, whole] of [
    ["speed", "Speaking speed", bounds.speed, false],
    ["backgroundVolume", "Noise volume", bounds.backgroundVolume, false],
    ["maxDurationS", "Call length cap", bounds.maxDurationS, false],
    ["silenceTimeoutS", "Final silence timeout", bounds.silenceTimeoutS, false],
    ["initialSilenceTimeoutS", "Initial silence timeout", bounds.initialSilenceTimeoutS, false],
    ["maxSilenceNudges", "Silence check-ins", bounds.maxSilenceNudges, true],
    ["endpointingMinDelayS", "Min response delay", bounds.endpointingMinDelayS, false],
    ["endpointingMaxDelayS", "Max endpointing delay", bounds.endpointingMaxDelayS, false],
    ["interruptionMinDurationS", "Min interruption duration", bounds.interruptionMinDurationS, false],
    ["interruptionMinWords", "Min interruption words", bounds.interruptionMinWords, true],
    ["vadActivationThreshold", "Background activation", bounds.vadActivationThreshold, false],
    ["firstMessageDelayS", "First message delay", bounds.firstMessageDelayS, false],
  ]) {
    const value = toNumber(form[key]);
    if (!within(value, bound)) {
      errors.push(`${label} must be between ${bound.min} and ${bound.max}.`);
    } else if (whole && !Number.isInteger(value)) {
      errors.push(`${label} must be a whole number.`);
    }
  }
  const minDelay = toNumber(form.endpointingMinDelayS);
  const maxDelay = toNumber(form.endpointingMaxDelayS);
  if (minDelay !== null && maxDelay !== null && minDelay > maxDelay) {
    errors.push("Min response delay cannot exceed the max endpointing delay.");
  }

  for (const [key, label, max] of [
    ["greeting", "Greeting", bounds.greetingMaxChars],
    ["personality", "Custom tone", bounds.personalityMaxChars],
    ["promptAdditions", "Prompt additions", bounds.promptAdditionsMaxChars],
    ["voicemailMessage", "Voicemail message", bounds.voicemailMessageMaxChars],
    ["destinationName", "Transfer destination name", bounds.destinationNameMaxChars],
  ]) {
    if (typeof form[key] === "string" && form[key].length > max) {
      errors.push(`${label} must be at most ${max} characters.`);
    }
  }

  const greeting = String(form.greeting || "").trim();
  if (greeting && !findVariables(greeting).includes("first_name")) {
    errors.push("Greeting must ask for the borrower with {{first_name}}.");
  }
  for (const [key, label] of [
    ["greeting", "Greeting"],
    ["promptAdditions", "Prompt additions"],
    ["voicemailMessage", "Voicemail message"],
    ["destinationName", "Transfer destination name"],
  ]) {
    const unknown = unknownVariables(form[key], known);
    if (unknown.length) {
      errors.push(`${label} uses unknown variables: ${unknown.map((n) => `{{${n}}}`).join(", ")}.`);
    }
  }

  const rules = cleanExplanations(form.documentExplanations) || [];
  if (rules.length > bounds.documentExplanations.maxEntries) {
    errors.push(`Document explanations allow at most ${bounds.documentExplanations.maxEntries} rules.`);
  }
  if (rules.some((rule) => !rule.match || !rule.send)) {
    errors.push("Every document explanation needs a phrase to match and what to send.");
  }
  if (
    rules.some(
      (rule) =>
        rule.match.length > bounds.documentExplanations.matchMaxChars ||
        rule.send.length > bounds.documentExplanations.sendMaxChars,
    )
  ) {
    errors.push(
      `Document explanations allow ${bounds.documentExplanations.matchMaxChars} characters to match and ${bounds.documentExplanations.sendMaxChars} for what to send.`,
    );
  }

  const terms = splitTerms(form.keyTerms);
  if (terms.length > bounds.keyTerms.maxEntries) {
    errors.push(`Keyword boosting allows at most ${bounds.keyTerms.maxEntries} words.`);
  }
  if (terms.some((term) => term.length > bounds.keyTerms.maxChars)) {
    errors.push(`Each boosted keyword must be at most ${bounds.keyTerms.maxChars} characters.`);
  }

  if (form.voicemailLeaveMessage && !form.voicemailDetect) {
    errors.push("Leaving a voicemail needs voicemail detection switched on.");
  }
  if (form.voicemailLeaveMessage && !String(form.voicemailMessage || "").trim()) {
    errors.push("Leaving a voicemail needs a message.");
  }

  const forwarding = String(form.forwardingNumber || "").trim();
  if (forwarding && !E164.test(forwarding)) {
    errors.push("Forwarding number must be in +E.164 form, such as +12485550142.");
  }
  if (form.forwardImmediately && !forwarding) {
    errors.push("Forwarding immediately needs a forwarding number.");
  }
  const hoursStart = String(form.transferHoursStart || "").trim();
  const hoursEnd = String(form.transferHoursEnd || "").trim();
  if (Boolean(hoursStart) !== Boolean(hoursEnd)) {
    errors.push("Transfer hours need both a start and an end.");
  } else if (hoursStart && !(TIME_OF_DAY.test(hoursStart) && TIME_OF_DAY.test(hoursEnd))) {
    errors.push("Transfer hours must be 24-hour times such as 09:00.");
  } else if (hoursStart && hoursStart >= hoursEnd) {
    errors.push("Transfer hours must start before they end.");
  }

  if (form.timezone && !isValidTimezone(form.timezone.trim())) {
    errors.push("Time zone must be an IANA name such as America/New_York.");
  }

  return errors;
}

/** The settings patch the backend takes, grouped as stored, from a valid form. */
export function buildVoiceSettingsPatch(form) {
  return {
    models: { stt: form.stt, llm: form.llm },
    voice: {
      key: form.voiceKey,
      speed: toNumber(form.speed),
      expressive: form.expressiveSpeech !== false,
    },
    sound: {
      background: form.backgroundSound,
      volume: toNumber(form.backgroundVolume),
      thinkingSound: form.thinkingSound === true,
    },
    wording: {
      agentType: form.agentType,
      greeting: (form.greeting || "").trim(),
      personalityPreset: form.personalityPreset || "",
      personality: (form.personality || "").trim(),
      promptAdditions: (form.promptAdditions || "").trim(),
      deferFigures: form.deferFigures !== false,
      keyTerms: splitTerms(form.keyTerms),
      documentExplanations: cleanExplanations(form.documentExplanations),
    },
    analysis: {
      postCall: form.postCall !== false,
      sentiment: form.sentiment === true,
    },
    voicemail: {
      detect: form.voicemailDetect === true,
      leaveMessage: form.voicemailLeaveMessage === true,
      message: (form.voicemailMessage || "").trim(),
    },
    call: {
      maxDurationS: toNumber(form.maxDurationS),
      silenceTimeoutS: toNumber(form.silenceTimeoutS),
      initialSilenceTimeoutS: toNumber(form.initialSilenceTimeoutS),
      maxSilenceNudges: toNumber(form.maxSilenceNudges),
      turnDetection: form.turnDetection,
      endpointingMinDelayS: toNumber(form.endpointingMinDelayS),
      endpointingMaxDelayS: toNumber(form.endpointingMaxDelayS),
      interruptionMinDurationS: toNumber(form.interruptionMinDurationS),
      interruptionMinWords: toNumber(form.interruptionMinWords),
      vadActivationThreshold: toNumber(form.vadActivationThreshold),
      whoSpeaksFirst: form.whoSpeaksFirst,
      firstMessageDelayS: toNumber(form.firstMessageDelayS),
      preemptiveSpeech: form.preemptiveSpeech === true,
      recording: form.recording === true,
      consentLine: form.consentLine === true,
      identityCheck: form.identityCheck !== false,
    },
    transfer: {
      forwardingNumber: (form.forwardingNumber || "").trim(),
      forwardImmediately: form.forwardImmediately === true,
      briefing: form.transferBriefing,
      destinationName: (form.destinationName || "").trim(),
      hoursStart: (form.transferHoursStart || "").trim(),
      hoursEnd: (form.transferHoursEnd || "").trim(),
    },
    callingHours: { timezone: (form.timezone || "").trim() },
  };
}

/** Whether a catalogue voice understands delivery marks such as [pause]. */
export function supportsExpressiveSpeech(voice) {
  return voice?.provider === "xai";
}

/** The catalogue entry for a key, or a stand-in so the card can still name it. */
export function voiceByKey(voices, key) {
  const found = (voices || []).find((voice) => voice.key === key);
  if (found) {
    return found;
  }
  const [provider, ...rest] = String(key || "").split(":");

  return { key, provider, id: rest.join(":"), name: rest.join(":") || "—", description: "" };
}

/** "Ara (xAI)" for the card and the recent-calls table. */
export function voiceLabel(voice) {
  if (!voice?.name) {
    return "—";
  }
  const provider = voice.providerLabel || { azure: "Azure", google: "Google", xai: "xAI" }[voice.provider];

  return provider ? `${voice.name} (${provider})` : voice.name;
}

/** One line per call for the card's "recent calls" table. */
export function describeRecentCall(call, voices = []) {
  const outcome = call?.outcome || {};
  const models = outcome.models || {};
  const voiceName = models.tts_voice
    ? voiceLabel(
        (voices || []).find((voice) => voice.id === models.tts_voice) || {
          name: models.tts_voice,
        },
      )
    : modelLabel(models.tts);

  return {
    id: call?.callId || "—",
    when: call?.placedAt || call?.createdAt || null,
    outcome: callOutcomeLabel(outcome.dialOutcome),
    tone: outcome.dialOutcome === "answered" ? "ok" : "warn",
    duration: formatCallDuration(outcome.durationS),
    cost: formatCost(outcome.costEstimateUsd),
    voice: voiceName,
    brain: modelLabel(models.llm),
    sentiment: SENTIMENT_LABELS[outcome.summary?.sentiment] || "",
    marked: call?.documentsMarkedRequested === true,
  };
}
